import React from 'react';
import { Wrench, ArrowRight } from 'lucide-react';
import { useDbNavigate } from '../../context/DatabaseContext';

const toolGroups = [
  {
    id: 'utilities-tools',
    label: 'Utilities',
    icon: Wrench,
    tools: [
      {
        slug: 'import-template',
        label: 'Bulk Import',
        description: 'Import records in bulk via Excel template. Validates data against existing records, resolves FK display values to IDs, and executes INSERT/UPDATE operations across all configured tables.'
      },
      {
        slug: 'power-query',
        label: 'Power Query',
        description: 'Ad-hoc query interface for direct database exploration. Build and run queries against project data with configurable output and export options.'
      }
    ]
  }
];

const UtilitiesDashboardPage = () => {
  const navigate = useDbNavigate();

  const totalTools = toolGroups.reduce((sum, g) => sum + g.tools.filter(t => !t.future).length, 0);

  return (
    <div className="page-container" style={{ padding: '20px' }}>
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: 'var(--spacing-sm) var(--spacing-md)',
        backgroundColor: 'var(--color-bg-white)',
        borderBottom: '1px solid var(--color-gray-200)',
        marginBottom: 'var(--spacing-lg)'
      }}>
        <Wrench size={20} style={{ marginRight: '8px', color: 'var(--color-primary)' }} />
        <h1 style={{ margin: 0, fontSize: 'var(--text-lg)', color: 'var(--color-primary)' }}>
          Utilities
        </h1>
        <span style={{ marginLeft: '12px', fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
          {totalTools} tools available
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xl)' }}>
        {toolGroups.map(group => {
          const GroupIcon = group.icon;
          return (
            <div key={group.id}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                marginBottom: 'var(--spacing-md)',
                paddingBottom: 'var(--spacing-sm)',
                borderBottom: '2px solid var(--color-primary)'
              }}>
                <GroupIcon size={16} style={{ color: 'var(--color-primary)' }} />
                <span style={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 700,
                  color: 'var(--color-primary)',
                  fontFamily: "'Rajdhani', 'Roboto Condensed', sans-serif",
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  {group.label}
                </span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)' }}>
                  {group.tools.filter(t => !t.future).length} {group.tools.filter(t => !t.future).length === 1 ? 'tool' : 'tools'} available
                </span>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: 'var(--spacing-md)'
              }}>
                {group.tools.map(tool => (
                  <div
                    key={tool.slug}
                    onClick={() => !tool.future && navigate(`/${tool.slug}`)}
                    style={{
                      background: 'var(--color-bg-white)',
                      border: '1px solid var(--color-gray-200)',
                      borderRadius: '6px',
                      padding: 'var(--spacing-md) var(--spacing-lg)',
                      cursor: tool.future ? 'default' : 'pointer',
                      opacity: tool.future ? 0.5 : 1,
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}
                    onMouseEnter={e => {
                      if (!tool.future) {
                        e.currentTarget.style.borderColor = 'var(--color-primary)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                      }
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = 'var(--color-gray-200)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        fontSize: 'var(--text-base)',
                        fontWeight: 600,
                        color: 'var(--color-primary)',
                        fontFamily: "'Rajdhani', 'Roboto Condensed', sans-serif",
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {tool.label}
                      </span>
                      {!tool.future && <ArrowRight size={16} style={{ color: 'var(--color-gray-400)', flexShrink: 0 }} />}
                      {tool.future && (
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', fontStyle: 'italic' }}>
                          Coming soon
                        </span>
                      )}
                    </div>
                    <p style={{
                      margin: 0,
                      fontSize: 'var(--text-sm)',
                      color: 'var(--color-gray-600)',
                      lineHeight: 1.5
                    }}>
                      {tool.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UtilitiesDashboardPage;
